/**
 * Permission types for the sharing system.
 * These match the backend API at /api/v1/permissions
 */
import { RELATION_VALUES, type RelationType } from '../../../generated/config-enums';

// Relation enum is single-sourced from shared/config/permissions.config.json
// (P-9 / D16) — re-exported here so existing `from './types'` imports keep
// working. Never hand-write the value list, ordering, or colour/label maps.
export type { RelationType };
export type SubjectType = 'user' | 'group' | 'company' | 'role';

export interface Subject {
  type: SubjectType;
  id: string;
  name?: string;
  email?: string;
  avatar_url?: string | null;
  /** True/false for a user subject, null/undefined otherwise. Drives the admin
   *  ownership-recovery affordance in ShareDialog (design §4.1). */
  is_active?: boolean | null;
}

export interface Resource {
  type: string;
  id: string;
}

export interface GrantAccessRequest {
  subject: Subject;
  relation: RelationType;
  resource: Resource;
  expires_at?: string;
}

export interface RevokeAccessRequest {
  subject: Subject;
  relation?: RelationType;
  resource: Resource;
}

export interface AccessEntry {
  subject: {
    type: SubjectType;
    id: string;
    name?: string;
    email?: string;
    avatar_url?: string | null;
    is_active?: boolean | null;
  };
  relation: RelationType;
  source: 'direct' | 'rule' | 'inherited';
  source_detail?: string;
  granted_at?: string;
  expires_at?: string;
}

export interface ParentEntity {
  type: string;
  id: string;
  name?: string;
}

export interface AccessListResponse {
  access: AccessEntry[];
  inherited_access?: AccessEntry[];
  parent_entity?: ParentEntity;
}

export interface PermissionCheckResponse {
  allowed: boolean;
  relation?: RelationType;
  source?: string;
  source_detail?: string;
}

/**
 * Hierarchy of relations - higher index = more permissions. Single-sourced from
 * the config enum (viewer < commenter < editor < owner); do not re-order by hand.
 */
export const RELATION_HIERARCHY: readonly RelationType[] = RELATION_VALUES;
